import { format } from 'date-fns';
import saveAs from 'file-saver';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '../../../apps/Home/components/TemporalBanner';
import { ReactComponent as TV } from '../../../assets/img/icons/tv.svg';
import { ReactComponent as FileDoc } from '../../../assets/svg/file-doc.svg';
import { ReactComponent as Filter } from '../../../assets/svg/filter.svg';
import { ACCOUNT_TYPES, ORDER_STATUSES } from '../../../const';
import { useAuth } from '../../../hooks';
import { useGetXlsMutation } from '../../../services/api';
import {
    AccessPermissionStatus,
    AccessRequestTask,
    AccessRequestType,
    AccessResponse,
    ContentType,
    LicenseAgreement,
    useAccessRequestsSetStatusMutation,
    useGetAccessRequestsAccountsQuery,
    useLazyGetAccessRequestAllowedRecipientsQuery,
    useLazyGetAccessRequestQuery,
    useLazyGetAccessRequestsQuery
} from '../../../services/content_v1';
import { AccessRequestStatus, AccountResType } from '../../../services/users_v1';
import { OuterMenuProps } from '../../../types/index';
import { useAlert } from '../../alert';
import { extractErrorDetail, toMplatDate, useIsMedialogistic, useIsProvider } from '../../common';
import Button from '../Button/Button';
import SplitButton from '../Button/SplitButton';
import { CheckBox } from '../CheckBox';
import Table from '../DataTable/Table';
import TableBody from '../DataTable/TableBody';
import { TableColumn } from '../DataTable/TableColumn';
import TableHeader from '../DataTable/TableHeader';
import { TableHeaderColumn } from '../DataTable/TableHeaderColumn';
import TableListName from '../DataTable/TableListName';
import TableRow from '../DataTable/TableRow';
import { DatePicker } from '../DatePicker';
import Header1 from '../Header1';
import ImageLoader from '../ImageLoader';
import MainContentFilter from '../MainContentFilter';
import { MobileDateFilterItem } from '../MobileDateFilterItem';
import MobileFilter from '../MobileFilter';
import { MobileFilterItem } from '../MobileFilterItem';
import { Modal } from '../Modal';
import { CommentModalDialog } from '../Modals/CommentModal';
import { ErrorModal } from '../Modals/ErrorModal';
import MultiSelect from '../MultiSelect';
import NoHideEmailAdmin from '../NoHide/NoHideEmailAdmin';
import NoHideFIOAdmin from '../NoHide/NoHideFIOAdmin';
import NoHidePhoneAdmin from '../NoHide/NoHidePhoneAdmin';
import { Searcher } from '../Searcher';
import Select from '../Select';
import { SelectButton } from '../SelectButtonSpan';
import { Spinner } from '../Spinner';


export interface OrderRowProviderStatusProps {
    order: AccessResponse,
    children?: React.ReactNode,
}

function OrderRowProviderStatus({ order, children }: OrderRowProviderStatusProps) {
    // const auth = useAuth();

    const [error, setError] = useState<string>('');
    const [errorTitle] = useState<string>(t('order:ORDER_CHANGE_STATUS_ERROR'));

    // const OPERATION_NAME: StringMap = {
    //     'open': t('Orders operation open'),
    //     'pending': t('Orders operation pending'),
    //     'granted': t('Orders operation grant'),
    //     'rejected': t('Orders operation reject'),
    // }

    const availResolutions = useMemo(() => ([
        { value: AccessRequestStatus.OPEN, label: t(ORDER_STATUSES[AccessRequestStatus.OPEN]) },
        { value: AccessRequestStatus.PENDING, label: t(ORDER_STATUSES[AccessRequestStatus.PENDING]) },
        { value: AccessRequestStatus.GRANTED, label: t(ORDER_STATUSES[AccessRequestStatus.GRANTED]) },
        { value: AccessRequestStatus.REJECTED, label: t(ORDER_STATUSES[AccessRequestStatus.REJECTED]) },
    ]), [t]);

    const [resolution, setResolution] = useState(order.status);
    const [setStatus] = useAccessRequestsSetStatusMutation();

    function postResolution(id: number, resolutionStatus: AccessRequestStatus) {
        if (resolutionStatus === AccessRequestStatus.PENDING) {
            setStatus({ ar_id: id, acc_id: order.account_id, status: AccessRequestStatus.PENDING }).unwrap().then(() => {
                setResolution(resolutionStatus);
            }).catch(e => {
                setError(extractErrorDetail(e));
            })
        }
        if (resolutionStatus === AccessRequestStatus.GRANTED) {
            setStatus({ ar_id: id, acc_id: order.account_id, status: AccessRequestStatus.GRANTED }).unwrap().then(() => {
                setResolution(resolutionStatus);
            }).catch(e => {
                setError(extractErrorDetail(e));
            })
        }
        if (resolutionStatus === AccessRequestStatus.REJECTED) {
            setStatus({ ar_id: id, acc_id: order.account_id, status: AccessRequestStatus.REJECTED }).unwrap().then(() => {
                setResolution(resolutionStatus);
            }).catch(e => {
                setError(extractErrorDetail(e));
            })
        }
    }

    if (order.status === AccessRequestStatus.GRANTED
        || order.status === AccessRequestStatus.CANCELED
        || order.status === AccessRequestStatus.REJECTED)
        return <>
            <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />
            <span className="orders-list__status-label">{t(ORDER_STATUSES[order.status])}</span>
        </>;
    return <>
        <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />
        <Select text value={resolution} options={availResolutions}
            onChange={(v) => postResolution(order.id, v)} top minimized>
            {children}
        </Select>
    </>;
}


function getOrderDate(order: AccessResponse) {
    if (order.added) {
        const timeStr = order.added;
        const dt = Date.parse(timeStr);
        // 14.05.2022 · 18:43
        return format(dt, 'dd.MM.yyyy · HH:mm');
    } else
        return '';
}

function getOrderDateForSort(order: AccessResponse) {
    if (order.added) {
        return order.added;
    } else
        return '';
}

export interface OrdersTableRowProps {
    order: AccessResponse,
    onCreatePermission: (id: number, acc_id: number) => void
}

function OrdersTableRow({ order, onCreatePermission }: OrdersTableRowProps) {
    const alert = useAlert();
    const auth = useAuth();
    const isProvider = useIsProvider();
    const isMobile = useMediaQuery('(max-width: 760px)');

    const [setStatus] = useAccessRequestsSetStatusMutation();

    const [showComment, setShowComment] = useState(false);
    const [task, setTask] = useState<AccessRequestTask | undefined>(undefined);

    const firstUnit = order.units.at(0);
    const coverPath = firstUnit && firstUnit.covers.length > 0 ? firstUnit.covers[0] : '';
    const disabled = order.status === AccessRequestStatus.REJECTED || order.status === AccessRequestStatus.CANCELED;
    const subject = order.consumer;

    const availTasks = useMemo(() => {
        return [
            { value: AccessRequestTask.NOTSET, label: '-' },
            { value: AccessRequestTask.CONTACT, label: t('CONTACT') },
            { value: AccessRequestTask.PROPOSAL, label: t('PROPOSAL') },
            { value: AccessRequestTask.CONCLUDE, label: t('CONCLUDE') },
            { value: AccessRequestTask.WAIT, label: t('WAIT') },
            { value: AccessRequestTask.DISCINSIDE, label: t('DISCINSIDE') },
        ];
    }, [t]);

    useEffect(() => {
        setTask(order.task || undefined);
    }, [order])

    return (
        <TableRow className={`${(disabled || ((order.r_type & R_TYPES.RECIPIENT) !== R_TYPES.RECIPIENT)) ? '_status-set' : ''}`}>
            <CommentModalDialog
                isOpen={showComment}
                onClose={() => setShowComment(false)}
                comment={order.comment || ''}
                action={(c) => setStatus({ ar_id: order.id, acc_id: order.account_id, comment: c })}
            />

            <TableColumn className="_id">{order.id}</TableColumn>
            <TableColumn className="_status">
                {(!isProvider || order.artype === AccessRequestType.TV)
                    ?
                    <>
                        <span className="orders-list__status-label">{t(ORDER_STATUSES[order.status])}</span>
                        {(order.status === AccessRequestStatus.PENDING || order.status === AccessRequestStatus.OPEN) &&
                            ((order.r_type & R_TYPES.RECIPIENT) === R_TYPES.RECIPIENT) &&
                            <Select minimized text value={task} options={availTasks} onChange={(v) => {
                                if (order.status === AccessRequestStatus.OPEN) {
                                    setStatus({
                                        acc_id: order.account_id,
                                        ar_id: order.id,
                                        status: AccessRequestStatus.PENDING,
                                    }).unwrap().then(() => {
                                        setStatus({
                                            acc_id: order.account_id,
                                            ar_id: order.id,
                                            task: v,
                                        }).unwrap().then(() => { setTask(v) }).catch(() => setTask(order.task || undefined))
                                    }).catch(() => setTask(order.task || undefined));
                                } else {
                                    setStatus({
                                        acc_id: order.account_id,
                                        ar_id: order.id,
                                        task: v,
                                    }).unwrap().then(() => { setTask(v) }).catch(() => setTask(order.task || undefined))
                                }
                            }} placeholder={t('Choose a task')} />
                        }
                    </>
                    :
                    <OrderRowProviderStatus order={order}>
                        {(order.status === AccessRequestStatus.PENDING || order.status === AccessRequestStatus.OPEN) &&
                            ((order.r_type & R_TYPES.RECIPIENT) === R_TYPES.RECIPIENT) &&
                            <Select minimized text value={task} options={availTasks} onChange={(v) => {
                                if (order.status === AccessRequestStatus.OPEN) {
                                    setStatus({
                                        acc_id: order.account_id,
                                        ar_id: order.id,
                                        status: AccessRequestStatus.PENDING,
                                    }).unwrap().then(() => {
                                        setStatus({
                                            acc_id: order.account_id,
                                            ar_id: order.id,
                                            task: v,
                                        }).unwrap().then(() => { setTask(v) }).catch(() => setTask(order.task || undefined))
                                    }).catch(() => setTask(order.task || undefined));
                                } else {
                                    setStatus({
                                        acc_id: order.account_id,
                                        ar_id: order.id,
                                        task: v,
                                    }).unwrap().then(() => { setTask(v) }).catch(() => setTask(order.task || undefined));
                                }
                            }} placeholder={t('Choose a task')} />
                        }
                    </OrderRowProviderStatus>
                }
            </TableColumn>
            <TableColumn className="_subscriber">
                <div className="orders-list__subscriber">
                    {!isMobile && subject.logo &&
                        <ImageLoader url={subject.logo} className="orders-list__subscriber-logo" />
                    }
                    <strong className="orders-list__subscriber-name">
                        {order.is_favorites &&
                            <span className="catalog-card__controls-status _min _vip"
                                title={t('account:VIP_ACCESS')}>VIP
                            </span>
                        }
                        <Link to={`/accounts/view/${order.consumer.id}`} className="link-accent">
                            {order.consumer.name ?? ''}
                        </Link> · {order.consumer.org.name}
                    </strong>

                    <span className="orders-list__subscriber-rep">
                        <NoHideFIOAdmin user={subject.owner} />
                    </span>
                    {' · '}
                    <NoHidePhoneAdmin user={subject.owner} />
                    {' · '}
                    <NoHideEmailAdmin user={subject.owner} />
                    {/* TODO договор */}
                    {/* <div className="orders-list__subscriber-contract">
                        <div className="orders-list__subscriber-contract">
                            {order.contracts.map((c) => <Link to={`/deals/registry?contract=${c}`} className="link-accent">
                                <Trans>Contract</Trans> <DlArrow />
                            </Link>)}
                        </div>
                    </div> */}
                    {
                        order.recipient.id !== Number(auth.account)
                        && (
                            <div className="orders-list__subscriber-contract">
                                <b><Trans>Recipient</Trans>:</b> <Link className="link-accent" to={`/accounts/${order.recipient.id}`}>{order.recipient.name}</Link>
                            </div>
                        )
                    }
                </div>
            </TableColumn>
            <TableColumn className="_content">
                {order.artype === AccessRequestType.MEDIA ?
                    <TableListName noimg url={coverPath} link={`/content/media/${order.units.at(0)?.id || NaN}/`} title={order.units.at(0)?.title} />
                    : (order.artype === AccessRequestType.PRODUCT ?
                        <TableListName noimg url={order.products.at(0)?.cover || ''} link={`/content/service/${order.products.at(0)?.id || NaN}/`} title={order.products.at(0)?.name} />
                        :
                        <div className="orders__content">
                            <span className="orders__content-tv">
                                <TV />&nbsp;<Trans>order:TV_COUNT</Trans>: {order.units.length}
                            </span>
                        </div>)
                }
            </TableColumn>
            <TableColumn className="_comment">
                {order.comment ? <>{order.comment}<br /></> : ''}
                {order.comment ?
                    <span className="link link-accent" onClick={() => setShowComment(true)}><Trans>Edit</Trans><span className="__mobile"> <Trans>comment</Trans></span></span>
                    :
                    <span className="link link-accent _comment-add" onClick={() => setShowComment(true)}><Trans>Add</Trans><span className="__mobile"> <Trans>comment</Trans></span></span>
                }
            </TableColumn>
            <TableColumn className="_agreement">
                {order.units.at(0)?.ctype === ContentType.TV &&
                    <span className="orders__content-status">
                        <div className="orders-list__subscriber-cta">
                            {[AccessRequestStatus.OPEN, AccessRequestStatus.PENDING].includes(order.status) &&
                                ((order.r_type & R_TYPES.RECIPIENT) === R_TYPES.RECIPIENT) &&
                                // <a href={`mailto:${subject.owner.email}`}
                                //     className="btn _min _bd">{t('Orders operation reply')}
                                // </a>
                                <Button size="small" className="_bd" onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (order.status === AccessRequestStatus.OPEN) {
                                        setStatus({ ar_id: order.id, acc_id: order.account_id, status: AccessRequestStatus.PENDING }).unwrap().then(() => {
                                            console.log('Перевод в статус На рассмотрении');
                                        }).catch((err) => {
                                            alert.error(extractErrorDetail(err));
                                        })
                                    }
                                    onCreatePermission(order.id, order.account_id);
                                }}>
                                    {t('Orders operation reply')}
                                </Button>
                            }
                        </div>
                        {[AccessRequestStatus.GRANTED].includes(order.status) &&
                            <>
                                {order.permissions.filter((p) => p.status === AccessPermissionStatus.ISSUED).map((p) =>
                                    <Link key={p.id} to={`/access/permissions/${p.id}`}>
                                        <Trans>order:PERMISSION_ISSUED</Trans>
                                        {' '}
                                    </Link>
                                )}
                            </>
                            // <span className="link link-accent _js-orders-approved" onClick={(e) => {
                            //     e.preventDefault();
                            //     e.stopPropagation();
                            //     onCreatePermission(order.id);
                            // }}>
                            // </span>
                        }
                    </span>
                }
            </TableColumn>
            <TableColumn className="_date">{getOrderDate(order)}</TableColumn>
            {/* {order.units.at(0)?.ctype === ContentType.TV && <TableColumn className="_status">
                <Link to=""
                    className="catalog-list__teams-item"
                    style={{ backgroundColor: '#323657' }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCreatePermission(order.id);
                    }}
                >
                    Выдать разрешение
                </Link>
            </TableColumn>} */}
        </TableRow>
    )
}

function addSortProperties(order: AccessResponse) {
    return {
        ...order,
        sort_id: order.id,
        sort_status: ORDER_STATUSES[order.status],
        sort_subscriber: order.consumer?.name,
        sort_content: order.units.map((u) => u.title).join(', '),
        sort_date: getOrderDateForSort(order)
    }
}

function dynamicSort(property: string) {
    let sortOrder = 1;
    if (property[0] === '-') {
        sortOrder = -1;
        property = property.substr(1);
    }
    property = 'sort_' + property;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (a: any, b: any) {
        const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
        return result * sortOrder;
    }
}

interface PermissionCreateModalProps {
    isOpen: boolean,
    orderId: number,
    accId: number,
    onClose: () => void,
}

export function OrderProviderReplyModal({ isOpen, orderId, accId, onClose }: PermissionCreateModalProps) {
    const isMdlg = useIsMedialogistic();
    // const [createPermission] = useCreateAccessPermissionMutation();
    const [setStatus] = useAccessRequestsSetStatusMutation();
    const [fetchOrder, { data: order }] = useLazyGetAccessRequestQuery();
    const [fetchRecipients, { data: recipients }] = useLazyGetAccessRequestAllowedRecipientsQuery();
    // const auth = useAuth();
    const navigate = useNavigate();

    const [success, setSuccess] = useState<boolean>();
    const [showForward, setShowForward] = useState<boolean>(false);
    const [fail, setFail] = useState<string>();

    // const [licNum, setLicNum] = useState('');
    // const [licDate, setLicDate] = useState('');
    // const [qualities, setQualities] = useState<number[]>([]);

    const successClass = 'popup_message';

    useEffect(() => {
        if (isOpen) {
            fetchOrder({ ar_id: orderId, acc_id: accId });
            fetchRecipients({ ar_id: orderId, acc_id: accId });
            setSuccess(false);
            setFail('');
        }
    }, [isOpen]);

    return (
        <Modal className={success ? successClass : 'order__permission-popup'} isOpen={isOpen} onClose={() => onClose()}>
            <Modal isOpen={showForward} onClose={() => setShowForward(false)}>
                <div className="form">
                    <Header1>Выберите действие</Header1>
                    <div className="form__item" style={{ display: 'flex', justifyContent: 'center' }}>
                        <SplitButton
                            options={(recipients?.results || []).map((r) => ({ value: r.id, label: r.name }))}
                            onClick={() => {
                                console.log('default');
                            }}
                            onChange={(v) => {
                                if (order) {
                                    setStatus({
                                        ar_id: order.results.id,
                                        acc_id: order.results.account_id,
                                        status: AccessRequestStatus.FORWARDED,
                                        sendto: v,
                                    }).unwrap().then(() => {
                                        onClose();
                                    }).catch((e) => {
                                        console.warn(e);
                                    });
                                }
                            }}
                        >
                            <Trans>order:FORWARD_AND_CLOSE</Trans>
                        </SplitButton>
                    </div>
                    <div className="form__item" style={{ display: 'flex', justifyContent: 'center' }}>
                        <SplitButton
                            options={(recipients?.results || []).map((r) => ({ value: r.id, label: r.name }))}
                            onClick={() => {
                                console.log('default')
                            }}
                            onChange={(v) => {
                                if (order) {
                                    setStatus({
                                        ar_id: order.results.id,
                                        acc_id: order.results.account_id,
                                        status: AccessRequestStatus.COPIED,
                                        sendto: v,
                                    }).unwrap().then(() => {
                                        onClose();
                                    }).catch((e) => {
                                        console.warn(e);
                                    });
                                }
                            }}
                        >
                            <Trans>order:COPY_ORDER</Trans>
                        </SplitButton>
                    </div>
                </div>
            </Modal>
            <div className="order__permission">
                <div className="user-settings__labels">
                    <span className="user-settings__labels-item">{order?.results.consumer.id}.{order?.results.units_cnt}.{order?.results.provider.id}.request</span>
                    <span className="user-settings__labels-item">{toMplatDate(order?.results.added, true)}</span>
                    {fail}
                </div>
                <h2 className="popup__heading">
                    <Trans>order:ORDER_TV_FROM</Trans> <br />
                    <Link to="#" className="link-accent">{order?.results.consumer.name}</Link>
                    <br />
                    <small className="tv-content__order-list-item-theme">
                        {order?.results.additional?.sendby && <small style={{ color: 'red' }}>
                            {order.results.additional?.sendby.status === AccessRequestStatus.COPIED && t('order:ORDER_COPIED_FROM')}
                            {order.results.additional?.sendby.status === AccessRequestStatus.FORWARDED && t('order:ORDER_FORWARDED_FROM')}
                            {order.results.additional?.sendby.account.org.name || order.results.additional.sendby.account.name}
                        </small>}
                    </small>
                </h2>
                <div className="tv-content__order-list">
                    {order?.results.units.map((u) => <div key={u.id} className="tv-content__order-list-item">
                        <div className="empty-image">{u.covers.length > 0 && <ImageLoader url={u.covers[0]} />}</div>
                        <span className="tv-content__order-list-item-name">{u.title}</span>
                        <span className="tv-content__order-list-item-theme">{u.category?.name}</span>
                    </div>)}
                </div>

                <h3><Trans>order:ORDER_LIC</Trans></h3>
                {order?.results.details.licagr === LicenseAgreement.NO && t('No')}
                {order?.results.details.licagr === LicenseAgreement.YES && t('Yes')}
                {order?.results.details.licagr === LicenseAgreement.PARTIAL && t('order:ORDER_LIC_PARTIAL')}

                <h3><Trans>order:REQ_TEST_PERIOD</Trans></h3>
                {order?.results.details.ontest ? <p>{t('Yes')}</p> : <p>{t('No')}</p>}

                <h3><Trans>order:ORDER_REQ_WHO</Trans></h3>
                <p><Trans>{ACCOUNT_TYPES[order?.results.owner.rtype || 0]}</Trans></p>
                <p>
                    <NoHideFIOAdmin user={order?.results.person} />,
                    {' '}
                    <NoHidePhoneAdmin user={order?.results.person} />,
                    {' '}
                    <NoHideEmailAdmin user={order?.results.person} />
                </p>

                <h3><Trans>order:ORDER_OWNER</Trans></h3>
                <p>
                    <NoHideFIOAdmin user={order?.results.consumer.owner} />,
                    {' '}
                    <NoHidePhoneAdmin user={order?.results.consumer.owner} />,
                    {' '}
                    <NoHideEmailAdmin user={order?.results.consumer.owner} />
                </p>

                {(order?.results.details.promocodes.length || 0) > 0 && <>
                    <h3><Trans>order:ORDER_PROMO</Trans></h3>
                    <p>
                        {order?.results.details.promocodes.join(', ')}
                    </p>
                </>}

                <div className="order__permission-cta">
                    {!isMdlg && (
                        <div className="order__permission-cta-item">
                            <Button onClick={() => navigate(`/access/permissions/create/${order?.results.id}/${order?.results.account_id}`)}>
                                <Trans>order:PERMISSION_EDIT</Trans>
                            </Button>
                        </div>
                    )}
                    <div className="order__permission-cta-item">
                        <Button onClick={() => navigate(`/access/permissions/ext/create/${order?.results.id}/${order?.results.account_id}`)}>
                            <Trans>order:PERMISSION_EXT_EDIT</Trans>
                        </Button>
                    </div>
                    <div className="order__permission-cta-item">
                        <Button className="_bd" onClick={() => {
                            if (order) {
                                setStatus({
                                    ar_id: order.results.id,
                                    acc_id: order.results.account_id,
                                    status: AccessRequestStatus.REJECTED
                                }).unwrap().then(() => {
                                    onClose();
                                }).catch((e) => {
                                    console.warn(e);
                                });
                            }
                        }}>
                            <Trans>Reject</Trans>
                        </Button>
                    </div>
                    {!isMdlg && (recipients?.results.length || 0) > 0 &&
                        <div className="order__permission-cta-item">
                            <Button className="_bd" onClick={() => {
                                setShowForward(true);
                            }}>
                                <Trans>order:FORWARD</Trans>
                            </Button>
                            {/* <SplitButton
                                options={(recipients?.results || []).map((r) => ({ value: r.id, label: r.name }))}
                                onClick={() => {
                                    console.log('default')
                                }}
                                onChange={(v) => {
                                    if (order) {
                                        setStatus({
                                            ar_id: order.results.id,
                                            acc_id: auth.account,
                                            status: AccessRequestStatus.FORWARDED,
                                            sendto: v,
                                        }).unwrap().then(() => {
                                            onClose();
                                        }).catch((e) => {
                                            console.warn(e);
                                        });
                                    }
                                }}
                            >
                                <Trans>order:FORWARD</Trans>
                            </SplitButton> */}
                        </div>
                    }
                    {!isMdlg && <div className="order__permission-cta-item">
                        <span className="link link-accent" onClick={() => {
                            if (order && order.results.status !== AccessRequestStatus.PENDING) {
                                setStatus({
                                    ar_id: order.results.id,
                                    acc_id: order.results.account_id,
                                    status: AccessRequestStatus.PENDING,
                                }).unwrap().then(() => {
                                    onClose();
                                }).catch((e) => {
                                    console.warn(e);
                                });
                            } else {
                                onClose();
                            }
                        }}>
                            <Trans>order:RETURN_LATER</Trans>
                        </span>
                    </div>}
                </div>
            </div>
        </Modal >
    );
}

enum R_TYPES {
    CREATOR = 1, // "owner"
    CONSUMER = 2, // "consumer"
    PROVIDER = 4, // "provider"
    RECIPIENT = 8, // "recipient"
}

function DealContentProvider({ menu }: OuterMenuProps) {
    // const
    const PAGE_SIZE = 24;
    // hooks
    const [searchParams] = useSearchParams();
    const auth = useAuth();
    // fetches
    const [refetch, { data }] = useLazyGetAccessRequestsQuery();
    const { data: clients } = useGetAccessRequestsAccountsQuery();
    const [fetchXlsData, { data: xlsData }] = useGetXlsMutation();
    // states
    const [current, setCurrent] = useState(0);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(false);

    const [permIdDialog, setPermIdDialog] = useState(NaN);
    const [accId, setAccId] = useState(NaN);

    const [date, setDate] = useState<Date | undefined>();

    const [search, setSearch] = useState<string>('');
    const [companyFilter, setCompanyFilter] = useState<string[]>([]);
    const [cTypeTVFilter, setCTypeTVFilter] = useState<boolean>(false);
    const [cTypeContentFilter, setCTypeContentFilter] = useState<boolean>(false);

    const [ctypeFilter, setCTypeFilter] = useState<number[]>([]);
    const [withPromocodes, setWithPromocodes] = useState(false);

    const restoredMainFilter = sessionStorage.dealOrdersFilter
        ? (sessionStorage.dealOrdersFilter !== 'all' ? Number(sessionStorage.dealOrdersFilter) : 'all')
        : AccessRequestStatus.ACTIVE;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [mainFilter, setMainFilter] = useState<any>(restoredMainFilter);
    const [byId, setById] = useState('');
    const [ordering, setOrdering] = useState('-id');

    const [filterVisible, setFilterVisible] = useState(false);
    const [filterCount, setFilterCount] = useState<number>(0);

    const mainFilterItems = useMemo(() => [
        { value: 'all', label: t('Orders filter all'), counter: data?.results.totals['all'] },
        { value: AccessRequestStatus.ACTIVE, label: t('Active'), counter: (data?.results.totals['opened'] || 0) + (data?.results.totals['pending'] || 0) },
        { value: AccessRequestStatus.OPEN, label: t('Orders filter new'), counter: data?.results.totals['opened'] },
        { value: AccessRequestStatus.PENDING, label: t('Orders filter pending'), counter: data?.results.totals['pending'] },
        { value: AccessRequestStatus.GRANTED, label: t('Orders filter granted'), counter: data?.results.totals['granted'] },
        { value: AccessRequestStatus.REJECTED, label: t('Orders filter rejected'), counter: data?.results.totals['rejected'] },
        { value: AccessRequestStatus.ARCHIVED, label: t('Orders filter archived'), counter: (data?.results.totals['rejected'] || 0) + (data?.results.totals['granted'] || 0) },
    ], [data?.results, t]);

    const [companyList, setCompanyList] = useState<{
        value: string;
        label: string;
    }[]>([]);

    const onClean = useCallback(() => {
        setSearch('');
        setDate(undefined);
        setCompanyFilter([]);
        setCTypeFilter([]);
        setCTypeTVFilter(false);
        setCTypeContentFilter(false);
    }, []);

    // effects
    useEffect(() => {
        sessionStorage.dealOrdersFilter = mainFilter;
        setCurrent(0);
    }, [companyFilter, search, date, mainFilter, ordering]);

    useEffect(() => {
        const tmp: number[] = [];
        if (cTypeContentFilter) tmp.push(ContentType.UNIT);
        if (cTypeTVFilter) tmp.push(ContentType.TV);
        setCTypeFilter(tmp);
    }, [cTypeTVFilter, cTypeContentFilter]);

    useEffect(() => {
        setIsFetched(true);
        const receiver = companyFilter.join(',');
        const effectiveStatus = mainFilter === 'all' ? '' : (mainFilter === AccessRequestStatus.ARCHIVED ? '2&status=1' : (mainFilter === AccessRequestStatus.ACTIVE ? '0&status=3' : mainFilter));
        refetch({
            offset: current,
            limit: PAGE_SIZE,
            ordering,
            search,
            id: byId,
            artype: ctypeFilter.join(','),
            consumer_name: receiver,
            status: effectiveStatus,
            crdate: (date && `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`) || '',
            u: auth.user + auth.account,
            with_promocodes: withPromocodes ? '1' : '',
        })
            .then(() => setIsFetched(false))
            .catch(() => setIsFetched(false));
    }, [current, date, ordering, search, refetch, companyFilter, ctypeFilter, mainFilter, auth.user, auth.account, withPromocodes]);

    useEffect(() => {
        setHasMore(!!data?.results.next);
    }, [data?.results]);

    const handleNavigation = useCallback(() => {
        if ((document.body.scrollHeight - window.innerHeight) - window.scrollY < 300 && !isFetched) {
            if (hasMore) {
                setCurrent(data?.results.data.length || 0);
            }
        }
    }, [data?.results.data.length, hasMore, isFetched]);

    useEffect(() => {
        window.addEventListener('scroll', handleNavigation);

        return () => {
            window.removeEventListener('scroll', handleNavigation);
        };
    }, [handleNavigation]);

    useEffect(() => {
        const tmp: Record<string, string> = {};
        searchParams.forEach((v, k) => {
            tmp[k] = v;
        });
        if (tmp.status) {
            setMainFilter(tmp.status === 'all' ? 'all' : Number(tmp.status));
        }
        if (tmp.id) {
            setById(tmp.id);
            setMainFilter('all');
        }
    }, [searchParams]);

    useEffect(() => {
        if (xlsData) {
            saveAs(xlsData, 'orderlist.xls');
        }
    }, [xlsData]);

    useEffect(() => {
        const uniqueCompany = new Set<string>();
        clients?.results.filter((s) => s.rtype === AccountResType.CONSUMER).every(s => uniqueCompany.add(s.name));
        const result: { value: string, label: string }[] = [];
        uniqueCompany.forEach((c) => result.push({ value: c, label: c }));
        setCompanyList(result);
    }, [clients]);

    const filteredData = useMemo(() => {
        const tmp = data?.results.data.map(i => addSortProperties(i)) || [];
        tmp.sort(dynamicSort(ordering));
        return tmp;
    }, [data?.results.data, ordering]);

    useEffect(() => {
        setFilterCount(
            + (date ? 1 : 0)
            + companyFilter.length
            + ctypeFilter.length
        )
    }, [date, companyFilter.length, ctypeFilter.length]);

    return (<>
        <OrderProviderReplyModal isOpen={!isNaN(permIdDialog)} orderId={permIdDialog} accId={accId} onClose={() => setPermIdDialog(NaN)} />

        <MainContentFilter>
            {menu}
            <SelectButton sub value={mainFilter} items={mainFilterItems} onChange={(v) => { setById(''); setMainFilter(v); }}>
                <a href="#xls" className="main__types-file" target="_blank" onClick={(e) => {
                    e.preventDefault();
                    fetchXlsData('/be/api/content/access/requests/xlsx/');
                }}>
                    <FileDoc />
                    <Trans>Orders xls export</Trans>
                </a>
            </SelectButton>

            <div className="main__controls _no-sort">
                <div className="main__controls-filter">
                    <Searcher value={search} onChange={(v) => setSearch(v)} />
                    <div className="main__controls-filter-item">
                        <DatePicker value={date} placeholder={t('Orders filter date')} setValue={(v) => setDate(v)} />
                    </div>
                    <MultiSelect className="main__controls-filter-item _wide _pre-checkbox" placeholder={t('Orders filter consumer')}
                        value={companyFilter} options={companyList || []} setValue={(v) => setCompanyFilter(v)} minimized />
                    {/* <MultiSelect className="main__controls-filter-item" placeholder={'Тип контента'}
                        value={ctypeFilter} options={availCTypes} setValue={(v) => setCTypeFilter(v)} minimized /> */}
                    <div className="main__controls-filter-item _pre-checkbox">
                        <CheckBox value={cTypeTVFilter} label={t('account:SPEC_TV')} onChange={(v) => setCTypeTVFilter(v)} />
                    </div>
                    <div className="main__controls-filter-item">
                        <CheckBox value={cTypeContentFilter} label={t('account:SPEC_CONTENT')} onChange={(v) => setCTypeContentFilter(v)} />
                    </div>
                    <div className="main__controls-filter-item">
                        <CheckBox value={withPromocodes} label={t('order:WITH_PROMOCODES')} onChange={(v) => setWithPromocodes(v)} />
                    </div>
                    {(filterCount > 0 || search) && <div className="main__controls-filter-item">
                        <Button className="_bd" size="small" title={t('Clean')} onClick={onClean} style={{ lineHeight: '24px' }}>
                            🧹
                        </Button>
                    </div>}
                </div>
                <div className="main__controls-filter-trigger" onClick={() => setFilterVisible(!filterVisible)}>
                    <Filter />
                    {!!filterCount && <span className="main__controls-filter-trigger-counter">{filterCount}</span>}
                </div>
            </div>
        </MainContentFilter>

        <Table className="orders-list">
            <TableHeader>
                <TableHeaderColumn header="ID" field="id" className="_id" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Orders table status')} field="status" className="_status" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Orders table consumer')} field="consumer_name" className="_subscriber" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Product')} field="title" className="_content" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Comment')} field="title" className="_comment" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={''} field="title" className="_agreement" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Orders table date')} field="date" className="_date" ordering={ordering} setOrdering={setOrdering} />
            </TableHeader>
            <TableBody>
                {filteredData?.map(order => (
                    <OrdersTableRow key={`${order.id}+${order.account_id}`} order={order} onCreatePermission={(permId, acc) => { setPermIdDialog(permId); setAccId(acc); }} />
                ))}
            </TableBody>
            {isFetched && <Spinner />}
        </Table>

        <MobileFilter
            filterVisible={filterVisible}
            setFilterVisible={setFilterVisible}
            onClean={onClean}
        >
            <MobileDateFilterItem value={date} label={t('Orders filter date')}
                placeholder={t('Orders filter date placeholder')}
                setValue={(v) => setDate(v)} />
            <MobileFilterItem label={t('Orders filter consumer')}
                value={companyFilter} options={companyList || []} setValue={(v) => setCompanyFilter(v)} />
            <MobileFilterItem label={t('Type')}
                value={ctypeFilter}
                options={[{ value: 1, label: t('account:SPEC_CONTENT') }, { value: 2, label: t('account:SPEC_TV') }]}
                setValue={(v) => {
                    setCTypeTVFilter(v.includes(2));
                    setCTypeContentFilter(v.includes(1))
                }}
            />
        </MobileFilter>

    </>)
}

export default DealContentProvider;
